import { useEffect, useState, useMemo, useRef, type ReactNode } from 'react'
import { formatDateLong, formatDateShort, getWeekday, formatRange, uniqueLessonDates } from '@/lib/date-utils'
import { cn } from '@/utils/cn'
import { Zap, Info, X } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { teacherPriorityApi } from '@/services/api/teacherPriority'
import { useAbsenceTypes } from '@/hooks/useAbsenceTypes'
import type {
  DateRange, SelectionMode, SubstitutionRequestConfig,
  DirectorGateSettings, GatePreviewResponse, AbsenceType,
} from '@/types/teacher-request'
import { ruleApplies } from '@/utils/documentDue'
import { BrockenWeekPlan } from '@/components/teacher-request/BrockenWeekPlan'
import { GanztagWeekdayPlan } from '@/components/teacher-request/GanztagWeekdayPlan'
import { groupGanztagWeekdays } from '@/utils/ganztagWeekdayPlan'
import type { WeekRankMode } from '@/utils/brockenUnits'

interface CoverageBlockDraft {
  id: string
  date?: string | null
  timeSlotIds: string[]
  blockName: string | null
  blockType: 'full_day' | 'partial'
  blockRank: number
  packageId: string | null
  requestMode: 'sequential' | 'parallel' | null
  customThresholdMinutes: number | null
  backgroundId: string | null
  entries: { teacherId: string; rank: number; teacherName: string; teacherCode?: string; rating: number }[]
}

interface ReviewModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  coverageBlocks?: CoverageBlockDraft[]
  requestMode: 'sequential' | 'parallel'
  customThreshold: number | null
  onCustomThresholdChange?: (value: number | null) => void
  fallbackMode?: 'auto' | 'manual' | null
  onFallbackModeChange?: (value: 'auto' | 'manual' | null) => void
  cascadeStrategy?: 'full_day_first' | 'unfilled_only' | null
  onCascadeStrategyChange?: (value: 'full_day_first' | 'unfilled_only' | null) => void
  parallelDeadline: number | null
  onParallelDeadlineChange?: (value: number | null) => void
  messageToSubstitutes?: string
  onMessageToSubstitutesChange?: (value: string) => void
  isUrgent?: boolean
  onIsUrgentChange?: (value: boolean) => void
  absenceType?: AbsenceType | ''
  onAbsenceTypeChange?: (value: AbsenceType) => void
  reason?: string
  onReasonChange?: (value: string) => void
  documents?: File[]
  onDocumentsChange?: (files: File[]) => void
  selectedLessons?: {
    entryId: string
    date: string
    timeSlotId: string
    subjectName?: string
    klassName?: string
    startTime?: string
    endTime?: string
    subjectId?: string
  }[]
  selectedDate?: string
  weekRankMode?: WeekRankMode
  onWeekRankModeChange?: (mode: WeekRankMode) => void

  // D1/E5: Direktor-Gate (Modal-Öffnung E1, Infoline, Pflicht-Absenzart)
  directorEnabled?: boolean
  directorSettings?: DirectorGateSettings

  selectionMode?: SelectionMode
  dateRange?: DateRange | null

  requestFlow?: 'prioritized' | 'automatic'
  priorityTab?: 'full_day' | 'partial'
  qualifiedTeachersCount?: number
  requestConfig?: Partial<SubstitutionRequestConfig> & { current_pending_requests?: number }
  adminMode?: boolean
  skipGate?: boolean
  onSkipGateChange?: (value: boolean) => void
  gatePreviewFn?: (payload: {
    entry_ids: string[]
    date?: string | null
    date_to?: string | null
    lessons?: { entry_id: string; date: string }[]
    absence_type?: string | null
  }, signal?: AbortSignal) => Promise<GatePreviewResponse>
}

function teacherInitials(name: string): string {
  return name.split(/[, ]/).filter(Boolean).map(s => s[0]).join('').slice(0, 2).toUpperCase()
}

function resolveBlockDate(
  block: CoverageBlockDraft,
  selectedLessons: { entryId: string; date: string; timeSlotId: string }[] | undefined,
  selectedDate: string | undefined,
): string {
  if (block.date) return block.date
  const matching = (selectedLessons ?? []).filter(l => block.timeSlotIds.includes(l.timeSlotId))
  const dates = uniqueLessonDates(matching)
  if (dates.length === 1 && dates[0]) return dates[0]
  return selectedDate ?? ''
}

type DayGroup = {
  key: string
  date: string | null
  blocks: CoverageBlockDraft[]
  lessonCount: number
  teacherCount: number
}

function groupCoverageBlocksByDate(
  blocks: CoverageBlockDraft[],
  selectedLessons: { entryId: string; date: string; timeSlotId: string }[] | undefined,
  selectedDate: string | undefined,
): DayGroup[] {
  const map = new Map<string, CoverageBlockDraft[]>()
  const sorted = [...blocks].sort((a, b) => a.blockRank - b.blockRank)
  for (const block of sorted) {
    const key = resolveBlockDate(block, selectedLessons, selectedDate)
    const list = map.get(key) ?? []
    list.push(block)
    map.set(key, list)
  }
  return [...map.keys()]
    .sort((a, b) => {
      if (!a) return 1
      if (!b) return -1
      return a.localeCompare(b)
    })
    .map(key => {
      const dayBlocks = map.get(key) ?? []
      const teachers = new Set(dayBlocks.flatMap(b => b.entries.map(e => e.teacherId)))
      const slotCount = new Set(dayBlocks.flatMap(b => b.timeSlotIds)).size
      const fromLessons = (selectedLessons ?? []).filter(l => key && l.date === key).length
      return {
        key,
        date: key || null,
        blocks: dayBlocks,
        lessonCount: fromLessons || slotCount,
        teacherCount: teachers.size,
      }
    })
}

function TeacherEntryRow({ entry, showBorder }: {
  entry: CoverageBlockDraft['entries'][number]
  showBorder: boolean
}) {
  return (
    <div className={cn(
      "flex justify-between items-center px-4 py-2.5",
      showBorder && "border-b border-gray-100 dark:border-gray-800",
    )}>
      <div className="flex items-center gap-2.5 min-w-0">
        <div className="w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-xs font-bold text-blue-700 dark:text-blue-300 flex-shrink-0">
          {teacherInitials(entry.teacherName)}
        </div>
        <div className="min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[180px]">
            {entry.teacherName}
          </div>
          {entry.teacherCode && (
            <div className="text-xs text-gray-500 dark:text-gray-400">{entry.teacherCode}</div>
          )}
        </div>
      </div>
      <span className="text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded-full flex-shrink-0">
        #{entry.rank}
      </span>
    </div>
  )
}

function PriorityEntries({ entries }: { entries: CoverageBlockDraft['entries'] }) {
  const sorted = [...entries].sort((a, b) => a.rank - b.rank)
  if (sorted.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 px-4 py-2.5">Keine Priorität gesetzt — Rangliste nach Affinität. Der Tag bleibt in der Anfrage.</p>
    )
  }
  return (
    <div>
      {sorted.map((entry, idx) => (
        <TeacherEntryRow key={entry.teacherId} entry={entry} showBorder={idx < sorted.length - 1} />
      ))}
    </div>
  )
}

function PriorityBlockCard({ block }: { block: CoverageBlockDraft }) {
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-800 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {block.blockName || `Block ${block.blockRank}`}
          </span>
          {block.backgroundId && (
            <span className="text-xs text-blue-600 dark:text-blue-400">
              Fach: {block.blockName || 'Unbekannt'}
            </span>
          )}
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full">
          {block.timeSlotIds.length} Lekt.
        </span>
      </div>
      <PriorityEntries entries={block.entries} />
    </div>
  )
}

function StepNumber({ num }: { num: number }) {
  return (
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-100 dark:bg-blue-900/40 border-2 border-blue-200 dark:border-blue-800 flex items-center justify-center text-xs font-bold text-blue-600 dark:text-blue-400">
      {num}
    </div>
  )
}

function StepWrapper({ num, title, description, children }: { num: number; title: string; description?: string; children: ReactNode }) {
  return (
    <div className="flex gap-4 mb-6 last:mb-0">
      <StepNumber num={num} />
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>}
        <div className="mt-3">{children}</div>
      </div>
    </div>
  )
}

function RadioDot({ isSelected }: { isSelected: boolean }) {
  return (
    <div className={cn(
      "w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-colors",
      isSelected ? "border-blue-500" : "border-gray-300 dark:border-gray-600"
    )}>
      <div className={cn(
        "w-2.5 h-2.5 rounded-full bg-blue-500 transition-transform duration-150",
        isSelected ? "scale-100" : "scale-0"
      )} />
    </div>
  )
}

function ToggleCardGroup({ label, description, value, onChange, options }: {
  label: string
  description: string
  value: string
  onChange: (value: string) => void
  options: { value: string; title: string; desc: string }[]
}) {
  const handleKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault()
      const next = (idx + 1) % options.length
      const nextOpt = options[next]
      if (nextOpt) onChange(nextOpt.value)
      const cards = e.currentTarget.parentElement?.querySelectorAll('[role="radio"]')
      ;(cards?.[next] as HTMLElement)?.focus()
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault()
      const prev = (idx - 1 + options.length) % options.length
      const prevOpt = options[prev]
      if (prevOpt) onChange(prevOpt.value)
      const cards = e.currentTarget.parentElement?.querySelectorAll('[role="radio"]')
      ;(cards?.[prev] as HTMLElement)?.focus()
    }
  }

  return (
    <div className="mb-5 last:mb-0">
      <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{label}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-2.5 leading-relaxed">{description}</div>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label={label}>
        {options.map((opt, idx) => (
          <div
            key={opt.value}
            role="radio"
            aria-checked={value === opt.value}
            tabIndex={value === opt.value ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); onChange(opt.value) }
              else handleKeyDown(e, idx)
            }}
            className={cn(
              "flex items-start gap-3 p-3 border-2 rounded-xl cursor-pointer transition-all outline-none",
              "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1",
              value === opt.value
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 shadow-sm"
                : "border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-blue-200 dark:hover:border-blue-800 hover:bg-blue-50/30"
            )}
          >
            <RadioDot isSelected={value === opt.value} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">{opt.title}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{opt.desc}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export function ReviewModal({
  isOpen,
  onClose,
  onConfirm,
  coverageBlocks = [],
  requestMode: _requestMode,
  customThreshold,
  onCustomThresholdChange: _onCustomThresholdChange,
  fallbackMode,
  onFallbackModeChange,
  cascadeStrategy,
  onCascadeStrategyChange,
  parallelDeadline: _parallelDeadline,
  onParallelDeadlineChange: _onParallelDeadlineChange,
  messageToSubstitutes = '',
  onMessageToSubstitutesChange,
  isUrgent = false,
  onIsUrgentChange,
  absenceType = '',
  onAbsenceTypeChange,
  reason = '',
  onReasonChange,
  documents = [],
  onDocumentsChange,
  selectedLessons,
  selectedDate,
  weekRankMode = 'repeat',
  onWeekRankModeChange,
  directorEnabled = false,
  directorSettings,
  selectionMode = 'single',
  dateRange,
  requestFlow = 'automatic',
  priorityTab,
  requestConfig,
  adminMode = false,
  skipGate = true,
  onSkipGateChange,
  gatePreviewFn,
}: ReviewModalProps) {
  // E5: Gate-Preview für die dynamische Infoline (300 ms Debounce + Abort).
  const [preview, setPreview] = useState<GatePreviewResponse | null>(null)
  const [, setPreviewLoading] = useState(false)
  const [, setPreviewFailed] = useState(false)
  const previewTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const previewAbort = useRef<AbortController | null>(null)

  // Direktorkonfig v2 (Z6): dynamische Abwesenheitsarten (aktive, sortiert);
  // Fallback statische Default-Liste bei Lade-/Fehlerfall (R4).
  const { types: absenceOptions } = useAbsenceTypes()
  const lessonDates = useMemo(() => uniqueLessonDates(selectedLessons ?? []), [selectedLessons])

  useEffect(() => {
    if (!isOpen) return

    document.body.style.overflow = 'hidden'

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = ''
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  // Preview-Abhängigkeiten aus der Auswahl ableiten.
  const previewParams = useMemo(() => {
    if (!isOpen || !directorEnabled) return null
    const entryIds = (selectedLessons ?? []).map(l => l.entryId)
    if (entryIds.length === 0) return null
    let date: string | null = null
    let dateTo: string | null = null
    if (selectionMode === 'range' && dateRange?.from) {
      date = dateRange.from
      dateTo = dateRange.to ?? null
    } else {
      const lessonDates = (selectedLessons ?? []).map(l => l.date).filter(Boolean).sort()
      if (lessonDates.length > 0) {
        date = lessonDates[0] ?? null
        const last = lessonDates[lessonDates.length - 1]
        dateTo = last && last !== date ? last : null
      } else if (selectedDate) {
        date = selectedDate
      }
    }
    return {
      entry_ids: entryIds,
      date,
      date_to: dateTo,
      lessons: (selectedLessons ?? []).map(l => ({ entry_id: l.entryId, date: l.date })),
      absence_type: absenceType || null,
    }
  }, [isOpen, directorEnabled, selectedLessons, selectedDate, selectionMode, dateRange, absenceType])

  useEffect(() => {
    if (!previewParams) {
      setPreview(null)
      setPreviewFailed(false)
      return
    }
    if (previewTimer.current) clearTimeout(previewTimer.current)
    previewAbort.current?.abort()
    previewAbort.current = new AbortController()

    const signal = previewAbort.current.signal
    setPreviewLoading(true)
    setPreviewFailed(false)

    previewTimer.current = setTimeout(async () => {
      try {
        const fetcher = gatePreviewFn ?? teacherPriorityApi.getDirectorGatePreview
        const res = await fetcher(previewParams, signal)
        if (signal.aborted) return
        setPreview(res)
      } catch (e: unknown) {
        if (signal.aborted) return
        setPreview(null)
        setPreviewFailed(true)
      } finally {
        if (!signal.aborted) setPreviewLoading(false)
      }
    }, 300)

    return () => {
      if (previewTimer.current) clearTimeout(previewTimer.current)
      previewAbort.current?.abort()
    }
  }, [previewParams, gatePreviewFn])

  // Direktorkonfig v2 (Z6): Chips dynamisch aus der Schul-Liste (D3) statt
  // hartkodiert — Werte = stabile Keys, Label aus der Tabelle.
  // (C10: AbsenceType ist string — eigene Schul-Arten fliessen ohne Cast.)
  const ABSENCE_CHIPS: { value: AbsenceType; label: string }[] = absenceOptions.map(t => ({
    value: t.key,
    label: t.label,
  }))

  // Rules of Hooks: Diese abgeleiteten Werte und der ⌘/Strg+Enter-Effekt müssen
  // VOR dem Conditional-Return (`if (!isOpen) return null`) stehen. Stand der
  // useEffect dahinter, hatte das Modal im geschlossenen Zustand einen Hook
  // weniger als im geöffneten → «Rendered more hooks than during the previous
  // render» + ErrorBoundary-Crash beim Öffnen.
  const hasPriorities = coverageBlocks.some(b => (b.entries?.length ?? 0) > 0)

  const dayGroups = useMemo(
    () => groupCoverageBlocksByDate(coverageBlocks, selectedLessons, selectedDate),
    [coverageBlocks, selectedLessons, selectedDate],
  )
  const ganztagDays = useMemo(
    () => dayGroups
      .filter((g): g is typeof g & { date: string } => Boolean(g.date))
      .map(g => ({
        date: g.date,
        lessonCount: g.lessonCount,
        teacherCount: g.teacherCount,
        blocks: g.blocks.map(b => ({
          id: b.id,
          blockName: b.blockName,
          timeSlotIds: b.timeSlotIds,
          entries: b.entries,
        })),
      })),
    [dayGroups],
  )
  const hasWeekdayRepeat = useMemo(
    () => groupGanztagWeekdays(ganztagDays).some(s => s.dates.length > 1),
    [ganztagDays],
  )
  const isBrockenReview = requestFlow === 'prioritized' && (
    priorityTab === 'partial'
    || (priorityTab == null && coverageBlocks.some(b => b.blockType === 'partial' && (b.entries?.length ?? 0) > 0))
  )
  const isDayAccordion = !isBrockenReview && requestFlow === 'prioritized' && dayGroups.length >= 2

  const thresholdInvalid = customThreshold !== null && customThreshold !== undefined
    && (customThreshold < 5 || customThreshold > 1440 || !Number.isFinite(customThreshold))

  const directorGateOn = directorEnabled && directorSettings?.director_gate_enabled === true
  const absenceRequired = directorEnabled && !absenceType

  const docRule = absenceType ? directorSettings?.document_rules?.[absenceType] : undefined
  const durationDays = preview?.duration_days ?? 0
  const docApplies = Boolean(
    directorEnabled && ruleApplies(docRule?.due, docRule?.days ?? null, durationDays),
  )

  const submitDisabled = (requestFlow === 'prioritized' && !hasPriorities) || thresholdInvalid
    || absenceRequired

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        if (submitDisabled) {
          e.preventDefault()
          return
        }
        onConfirm()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onConfirm, submitDisabled])

  if (!isOpen) return null

  const showFallbackToggle = requestFlow === 'prioritized'
    && onFallbackModeChange !== undefined
    && requestConfig?.allow_teacher_override_fallback !== false

  const showStrategyToggle = false

  const showFallbackStep = showFallbackToggle || showStrategyToggle

  const showPrioritiesStep = requestFlow === 'prioritized' && coverageBlocks.length > 0

  const showDirectorStep = directorEnabled
  const showOptionsStep = onIsUrgentChange !== undefined || onMessageToSubstitutesChange !== undefined

  const MIME_OK = /\.(pdf|jpe?g|png|docx?|xlsx?)$/i
  const MAX_DOC_BYTES = 10 * 1024 * 1024

  const handleAddFiles = (files: FileList | null) => {
    if (!files || !onDocumentsChange) return
    const incoming = Array.from(files)
    const totalSize = documents.reduce((s, f) => s + f.size, 0) + incoming.reduce((s, f) => s + f.size, 0)
    if (totalSize > MAX_DOC_BYTES) return // Hinweis im UI
    const valid = incoming.filter(f => MIME_OK.test(f.name))
    onDocumentsChange([...documents, ...valid])
  }

  const handleRemoveFile = (index: number) => {
    if (!onDocumentsChange) return
    onDocumentsChange(documents.filter((_, i) => i !== index))
  }

  const visibleSteps = [true, showPrioritiesStep, showOptionsStep, showDirectorStep, showFallbackStep]
  const getStepNum = (idx: number) => {
    let n = 0
    for (let i = 0; i <= idx; i++) if (visibleSteps[i]) n++
    return n
  }

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-[2px] flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className={cn(
          "bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-h-[85vh] flex flex-col animate-modal-enter",
          isBrockenReview ? "max-w-2xl" : "max-w-xl",
        )}
        onClick={e => e.stopPropagation()}
      >

        <div className="px-7 pt-6 pb-0 flex justify-between items-start">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {isBrockenReview ? 'Die Woche, die Sie anfragen' : 'Überprüfen & Senden'}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {isBrockenReview
                ? 'Klick auf einen Brocken zeigt die Ränge. Extra-Wochen sind Akkordeons.'
                : isDayAccordion
                  ? hasWeekdayRepeat
                    ? 'Unabhängige Ganztage als Wochentag-Serien.'
                    : 'Unabhängige Tages-Anfragen. Öffnen Sie einen Tag, um die Rangliste zu prüfen.'
                  : <>Bitte bestätigen Sie die Details Ihrer{' '}
                    {requestFlow === 'automatic' ? 'Automatikanfrage' : 'Prioritätsanfrage'}.</>}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            aria-label="Schliessen"
          >
            <X className="w-5 h-5 text-gray-500 dark:text-gray-400" />
          </button>
        </div>

        <div className="px-7 py-5 overflow-y-auto flex-1">

          <StepWrapper num={getStepNum(0)} title="Details" description="Zusammenfassung Ihrer Anfrage">
            <div className="space-y-3">
              {selectionMode === 'range' && dateRange?.from && dateRange?.to ? (
                <div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{formatRange(dateRange.from, dateRange.to)}</div>
                  {lessonDates.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {lessonDates.map(d => (
                        <span key={d} className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-0.5 rounded-md">
                          {getWeekday(d)} {formatDateShort(d)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : selectedDate ? (
                <div className="text-sm text-gray-600 dark:text-gray-400">{formatDateLong(selectedDate)}</div>
              ) : null}

              {selectedLessons && selectedLessons.length > 0 && (
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  {selectedLessons.length} Lektion{selectedLessons.length !== 1 ? 'en' : ''}
                  {selectionMode === 'range' && lessonDates.length
                    ? ` über ${lessonDates.length} Tage`
                    : ''}
                </div>
              )}

              <div className="text-sm text-gray-600 dark:text-gray-400">
                {requestFlow === 'automatic' ? 'Automatisch' : 'Priorität'}
              </div>
            </div>
          </StepWrapper>

          {showPrioritiesStep && (
            <StepWrapper
              num={getStepNum(1)}
              title={isBrockenReview ? 'Brocken' : 'Prioritätsliste'}
              description={
                isBrockenReview
                  ? 'Zeile ist der Tag, Farbe die Serie'
                  : isDayAccordion
                    ? hasWeekdayRepeat
                      ? 'Serie pro Wochentag, nicht jeder Tag einzeln'
                      : 'Jeder Tag mit eigener Rangliste'
                    : 'Lehrpersonen nach Priorität'
              }
            >
              {isBrockenReview ? (
                <BrockenWeekPlan
                  lessons={selectedLessons ?? []}
                  blocks={coverageBlocks.filter(b => b.blockType === 'partial')}
                  mode={weekRankMode}
                  onModeChange={onWeekRankModeChange ?? (() => {})}
                  keepTogether={requestConfig?.keep_double_lessons_together !== 'off'}
                />
              ) : isDayAccordion ? (
                <GanztagWeekdayPlan days={ganztagDays} />
              ) : (
                <div className="space-y-3">
                  {coverageBlocks
                    .sort((a, b) => a.blockRank - b.blockRank)
                    .map(block => (
                      <PriorityBlockCard key={block.id} block={block} />
                    ))}
                </div>
              )}
            </StepWrapper>
          )}

          {showOptionsStep && (
          <StepWrapper num={getStepNum(2)} title="Optionen" description="Nachricht und Dringlichkeit">
            <div className="space-y-4">
              {onIsUrgentChange !== undefined && (
                <div className="flex items-center justify-between p-3 border border-red-200 dark:border-red-800 rounded-xl bg-red-50/50 dark:bg-red-900/10">
                  <span className="text-sm font-semibold text-red-600 dark:text-red-400">
                    EILIG – Dringende Anfrage
                  </span>
                  <Switch checked={isUrgent} onCheckedChange={onIsUrgentChange} className="data-[state=checked]:bg-red-500" />
                </div>
              )}

              {onMessageToSubstitutesChange !== undefined && (
                <div>
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-100 block mb-1.5">
                    Nachricht an Vertretungen
                  </label>
                  <textarea
                    value={messageToSubstitutes}
                    onChange={e => onMessageToSubstitutesChange(e.target.value)}
                    placeholder="Optionale Nachricht an die Vertretungslehrer..."
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                  />
                </div>
              )}
            </div>
          </StepWrapper>
          )}

          {showDirectorStep && (
            <StepWrapper num={getStepNum(3)} title="Abwesenheit" description="Art der Abwesenheit">
              {directorGateOn && preview?.triggered && (
                <div className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg px-3.5 py-2.5 mb-4">
                  Nach dem Senden prüft der Direktor, bevor jemand angefragt wird.
                  {adminMode && onSkipGateChange && (
                    <label className="flex items-start gap-2.5 mt-2.5 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={skipGate}
                        onChange={e => onSkipGateChange(e.target.checked)}
                        className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-600"
                      />
                      <span>Suche sofort starten</span>
                    </label>
                  )}
                </div>
              )}

              {/* Absenzart Pflicht-Chips (E4: keine «Keine Angabe»-Option) */}
              <div className="mb-4">
                <label className="text-sm font-semibold text-gray-900 dark:text-gray-100 block mb-1.5">
                  Abwesenheits-Art <span className="font-normal text-red-500 text-xs">* Pflicht</span>
                </label>
                <div className="flex flex-wrap gap-1.5">
                  {ABSENCE_CHIPS.map(chip => (
                    <button
                      key={chip.value}
                      type="button"
                      onClick={() => onAbsenceTypeChange?.(chip.value)}
                      className={cn(
                        "px-3 py-1.5 rounded-full text-sm border transition-colors",
                        absenceType === chip.value
                          ? "bg-indigo-600 text-white border-indigo-600 font-semibold"
                          : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600 hover:border-indigo-400"
                      )}
                    >
                      {chip.label}
                    </button>
                  ))}
                </div>
                {absenceRequired && (
                  <p className="text-xs text-red-600 mt-1">Bitte wählen Sie eine Abwesenheits-Art.</p>
                )}
              </div>

              {/* Grund (E12: nur Direktor sichtbar) */}
              {onReasonChange !== undefined && (
                <div>
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-100 block mb-1.5">
                    Bemerkung
                    <span className="font-normal text-gray-400 text-xs"> (optional)</span>
                  </label>
                  <textarea
                    value={reason}
                    onChange={e => onReasonChange(e.target.value)}
                    placeholder="z. B. Zeugniskonferenz, ärztliches Attest folgt, …"
                    maxLength={500}
                    className="w-full px-3 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm resize-none bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    rows={2}
                  />
                  <div className="text-right text-xs text-gray-400">{reason.length} / 500</div>
                </div>
              )}

              {docApplies && (
              <div className="pt-1">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    Dokumente <span className="font-normal text-gray-500 text-xs">— nachreichen möglich</span>
                  </label>
                </div>
                <label className="flex items-center justify-center gap-2 border-2 border-dashed border-indigo-300 dark:border-indigo-700 hover:border-indigo-400 rounded-lg px-4 py-3 cursor-pointer text-sm text-indigo-700 dark:text-indigo-300">
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={e => { handleAddFiles(e.target.files); e.target.value = '' }}
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                  />
                  📎 Datei wählen
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  PDF, JPG, PNG, Word/Excel · max. 10 MB gesamt. Einreichen ohne Datei ist erlaubt.
                </p>
                {documents.length > 0 && (
                  <ul className="mt-2 space-y-1.5">
                    {documents.map((f, i) => (
                      <li key={`${f.name}-${i}`} className="flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md px-3 py-2 text-sm">
                        <span className="truncate font-medium max-w-[220px]">{f.name}
                          <span className="ml-2 text-[11px] text-gray-400">{(f.size / 1024).toFixed(0)} KB</span>
                        </span>
                        <button type="button" onClick={() => handleRemoveFile(i)}
                          className="text-gray-400 hover:text-red-600 ml-2 font-bold">✕</button>
                      </li>
                    ))}
                  </ul>
                )}
                {documents.reduce((s, f) => s + f.size, 0) > MAX_DOC_BYTES && (
                  <p className="text-xs text-red-600 mt-1">Maximale Dokumenten-Größe von 10 MB überschritten.</p>
                )}
              </div>
              )}
            </StepWrapper>
          )}

          {showFallbackStep && (
            <StepWrapper num={getStepNum(4)} title="Fallback – Automatische Suche" description="Was passiert, wenn die Prioritätsliste erschöpft ist?">
              <div className="border border-blue-200 dark:border-blue-800 rounded-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 px-5 py-3.5 border-b border-blue-200 dark:border-blue-800">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-bold text-blue-900 dark:text-blue-200">
                      Automatische Suche nach Prioritätsliste
                    </span>
                  </div>
                </div>

                <div className="p-5">
                  {showFallbackToggle && (
                    <ToggleCardGroup
                      label="Soll die Suche automatisch erfolgen?"
                      description="«Danach Automatik»: sobald die Rangliste eines Tages erschöpft ist, sucht die Automatik für diesen Tag weiter. «Erst bestätigen»: kein Matcher, bis Sie die Suche für die ganze Anfrage freigeben. Wizard-Tage laufen unabhängig weiter."
                          value={fallbackMode ?? requestConfig?.priority_fallback_mode ?? 'auto'}
                          onChange={(v) => onFallbackModeChange?.(v as 'auto' | 'manual')}
                          options={[
                            {
                              value: 'auto',
                              title: 'Danach Automatik',
                              desc: 'Sobald die Rangliste eines Tages erschöpft ist, sucht die Automatik tagesweise weiter (Ganztag, dann Kombinationen).',
                            },
                            {
                              value: 'manual',
                              title: 'Erst bestätigen',
                              desc: 'Sie erhalten eine Benachrichtigung und müssen die automatische Suche explizit freigeben.',
                            },
                          ]}
                    />
                  )}

                  {showStrategyToggle && (
                    <div className={showFallbackToggle ? 'pt-5 border-t border-blue-100 dark:border-blue-900' : ''}>
                      <ToggleCardGroup
                        label="Wie soll die automatische Suche vorgehen?"
                        description="Gilt für die automatische Suche, sobald die Prioritätsliste erschöpft ist."
                        value={cascadeStrategy ?? 'full_day_first'}
                        onChange={(v) => onCascadeStrategyChange?.(v as 'full_day_first' | 'unfilled_only')}
                        options={[
                          {
                            value: 'full_day_first',
                            title: 'Ganztagssuche zuerst',
                            desc: 'Zuerst werden Lehrpersonen angefragt, die alle offenen Lektionen übernehmen könnten. Erst danach wird gezielt nach Kombinationen für einzelne unbesetzte Lektionen gesucht.',
                          },
                          {
                            value: 'unfilled_only',
                            title: 'Nur unbesetzte Lektionen',
                            desc: 'Es wird direkt nach Lehrpersonen bzw. Kombinationen für die unbesetzten Lektionen gesucht – ohne Ganztags-Pakete.',
                          },
                        ]}
                      />
                    </div>
                  )}

                  <div className="flex items-start gap-2 mt-4 pt-4 border-t border-blue-100 dark:border-blue-900">
                    <Info className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                      Wenn alle priorisierten Lehrpersonen abgelehnt haben oder nicht reagiert haben,
                      übernimmt die automatische Vertretungssuche (wie im Automatikmodus).
                    </span>
                  </div>
                </div>
              </div>
            </StepWrapper>
          )}

        </div>

        <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-800 flex justify-between items-center">
          <span className="text-xs text-gray-400 dark:text-gray-500">⌘ + Enter zum Bestätigen</span>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              Zurück
            </button>
            <button
              onClick={onConfirm}
              disabled={submitDisabled}
              className={cn(
                "px-6 py-2.5 rounded-xl text-sm font-semibold transition-all",
                submitDisabled
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600 shadow-sm hover:shadow-md"
              )}
            >
              Bestätigen & Senden
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
